import {
  isCustomAbutmentCharacteristics,
  isReferencedImplantCharacteristics,
  isStandardAbutmentCharacteristics,
  isUnreferencedImplantCharacteristics,
  type AbutmentCharacteristics,
  type ImplantCharacteristics,
  type ImplantedItemCharacteristics,
  type InlayCoreCharacteristics,
  type ItemType,
  type MaterialCharacteristics,
  type ShadeCharacteristics,
  type TeethArch,
  type WaxUpType,
} from "@jasper/client-models";
import type {
  CreateItemInput,
  CreateMaterialCharacteristicsInput,
  CreateMultiShadeCharacteristicsInput,
  CreateProductInput,
  CreateSingleShadeCharacteristicsInput,
  ItemPreferencesInput,
  ProductTypeV2,
  UpdateOrderProductInput,
} from "../../gql/generated/graphql";

type ItemArgsCharacteristics = {
  type: ItemType;
  materialCharacteristics?: MaterialCharacteristics;
  inlayCoreCharacteristics?: InlayCoreCharacteristics | null;
  waxUpType?: WaxUpType;
  implantedItemCharacteristics?: ImplantedItemCharacteristics;
  abutmentCharacteristics?: AbutmentCharacteristics;
  implantCharacteristics?: ImplantCharacteristics;
};

/**
 * Mirrors `CreateItemInput`: exactly one of `tooth` / `arch` identifies what the item
 * targets, which is what the API turns into the item scope. The `never` members make
 * the two mutually exclusive at the type level.
 */
export type ProductItemArgs =
  | (ItemArgsCharacteristics & { tooth: number; arch?: never })
  | (ItemArgsCharacteristics & { arch: TeethArch; tooth?: never });

export interface UpdateProductArgs {
  productId: string;
  items?: ProductItemArgs[] | null;
  itemPreferences?: ItemPreferencesInput | null;
  teeth?: number[] | null;
  type?: ProductTypeV2;
}

export interface CreateProductArgs {
  type: CreateProductInput["type"];
  teeth: number[];
  items: ProductItemArgs[];
}

const shadeCharacteristicsToCreateInput = (
  shadeCharacteristics: ShadeCharacteristics
):
  | { multiShadeCharacteristics: CreateMultiShadeCharacteristicsInput }
  | { shadeCharacteristics: CreateSingleShadeCharacteristicsInput } => {
  if (shadeCharacteristics.isMultiShade) {
    return {
      multiShadeCharacteristics: {
        baseShadeId: shadeCharacteristics.baseShade,
        incisalShadeId: shadeCharacteristics.incisalShade,
        gingivalShadeId: shadeCharacteristics.gingivalShade,
      },
    };
  } else {
    return {
      shadeCharacteristics: {
        shadeId: shadeCharacteristics.shade,
      },
    };
  }
};

const materialCharacteristicsToCreateInput = (
  materialCharacteristics: MaterialCharacteristics
): CreateMaterialCharacteristicsInput => {
  return {
    materialId: materialCharacteristics.material,
    ...("shadeCharacteristics" in materialCharacteristics &&
    materialCharacteristics.shadeCharacteristics
      ? shadeCharacteristicsToCreateInput(
          materialCharacteristics.shadeCharacteristics
        )
      : undefined),
  };
};

export const itemToCreateInput = (item: ProductItemArgs): CreateItemInput => ({
  tooth: item.tooth,
  arch: item.arch,
  type: item.type,
  waxUpType: item.waxUpType,
  materialCharacteristics: item.materialCharacteristics
    ? materialCharacteristicsToCreateInput(item.materialCharacteristics)
    : undefined,
  inlayCoreCharacteristics: item.inlayCoreCharacteristics
    ? {
        hasClavette: item.inlayCoreCharacteristics.hasClavette,
        scanPost: item.inlayCoreCharacteristics.scanPost,
      }
    : undefined,
  implantedItemCharacteristics: item.implantedItemCharacteristics,
  customAbutmentCharacteristics:
    item.abutmentCharacteristics &&
    isCustomAbutmentCharacteristics(item.abutmentCharacteristics)
      ? item.abutmentCharacteristics
      : undefined,
  standardAbutmentCharacteristics:
    item.abutmentCharacteristics &&
    isStandardAbutmentCharacteristics(item.abutmentCharacteristics)
      ? item.abutmentCharacteristics
      : undefined,
  referencedImplantCharacteristics:
    item.implantCharacteristics &&
    isReferencedImplantCharacteristics(item.implantCharacteristics)
      ? item.implantCharacteristics
      : undefined,
  unreferencedImplantCharacteristics:
    item.implantCharacteristics &&
    isUnreferencedImplantCharacteristics(item.implantCharacteristics)
      ? item.implantCharacteristics
      : undefined,
});

export const itemPreferencesToInput = (
  itemPreferences:
    (ItemPreferencesInput & { __typename?: string }) | null | undefined
): ItemPreferencesInput | undefined => {
  if (!itemPreferences) {
    return undefined;
  }

  return {
    occlusalContactTightness: itemPreferences.occlusalContactTightness,
    proximalContactTightness: itemPreferences.proximalContactTightness,
    noOcclusalClearance: itemPreferences.noOcclusalClearance,
    occlusalStaining: itemPreferences.occlusalStaining,
    interproximalContactDesign: itemPreferences.interproximalContactDesign,
    anteriorPosteriorPonticDesign:
      itemPreferences.anteriorPosteriorPonticDesign,
  };
};

export const updateProductArgsToInput = ({
  items,
  itemPreferences,
  teeth,
  type,
}: UpdateProductArgs): UpdateOrderProductInput => ({
  items: items ? items.map(itemToCreateInput) : undefined,
  itemPreferences,
  teeth,
  type,
});

export const createProductArgsToInput = ({
  type,
  teeth,
  items,
}: CreateProductArgs): CreateProductInput => ({
  type,
  teeth,
  items: items.map(itemToCreateInput),
});
